import React, { useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getIntegrations, setJobFunction, setPreferences } from "../../../apis/ext";
import { toast } from "react-hot-toast";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

import SharedDataContext from "../../../SharedDataContext";
import Input from "../../Tags/Input";
import { Button } from "../../ui/button";
import { jobFunctions } from "../../../constant";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { crmList } from "../../../axios/constant";
import { returnRecentPagePath, sendEventToloadBulkProfilePage } from "../../../utils/GenericFn";
import { useNavigate } from "react-router-dom";
import { useSetWorkspacePreferences } from "../../../apiHooks/useSetWorkspacePreferences";

export default function Preferences() {
  const navigate = useNavigate();
  const captureEvent = useCaptureEvent();
  const queryClient = useQueryClient();
  const { userData, params } = useContext(SharedDataContext);
  const [autoVerifyEmail, setAutoVerifyEmail] = useState(
    userData?.preferences?.chrome_ext_auto_verify_email ?? false
  );
  const [prefType, setPrefType] = useState("email_verify");
  const setPreferencesFn = useMutation("set_pref", setPreferences, {
    onSuccess(data, variables, context) {
      toast.success("Preferences updated successfully");
      queryClient.invalidateQueries("get_user");

      if (params?.bulkProfilePage) {
        navigate("/contact-viewer/bulk-profiles");
        sendEventToloadBulkProfilePage();
      } else {
        navigate(returnRecentPagePath(params));
      }
    },
    onError(error) {
      console.log(error);
    },
  });

  const setWorkspacePreferencesFn = useSetWorkspacePreferences(
    (data) => {
      console.log("✅ Preferences saved:", data);
      // maybe show toast

      if (params?.bulkProfilePage) {
        navigate("/contact-viewer/bulk-profiles");
        sendEventToloadBulkProfilePage();
      } else {
        navigate(returnRecentPagePath(params));
      }
    },
    (error) => {
      console.error("❌ Failed to save preferences:", error);
      // maybe show error toast
    }
  );

  const handleSetPreferences = (value, type = "email_verify") => {
    setPrefType(type);
    let payload = {};
    if (type === "email_verify") {
      payload = { chrome_ext_auto_verify_email: value };
      setAutoVerifyEmail(value);
    }
    if (type === "crm_select") {
      payload = { active_crm_workspace_integration_config_id: +value };
    }

    if (setPreferencesFn.isLoading) {
      return;
    } else {
      setPreferencesFn.mutate({
        data: {
          preferences: payload,
        },
      });
    }
  };

  useEffect(() => {
    if (userData && userData.preferences) {
      setAutoVerifyEmail(userData.preferences.chrome_ext_auto_verify_email);
    }
    if (userData) {
      setCurrentJobFunction(userData?.job_function);
      setCurrentJobTitle(userData?.job_title);
    }
  }, [userData]);

  // handle job function
  const [formHasError, setFormHasError] = useState(false);
  const [currentJobFunction, setCurrentJobFunction] = useState("");
  const [currentJobTitle, setCurrentJobTitle] = useState("");
  const setJobFunctionFn = useMutation("set_jobfunction", setJobFunction, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries("get_user");
      toast.success("AI preferences are saved successfully.");
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleSetJobFunction = () => {
    if (currentJobFunction === "") {
      setFormHasError(true);
      return;
    }
    if (currentJobTitle === "") {
      setFormHasError(true);
      return;
    }
    if (setJobFunctionFn.isLoading) {
      return;
    }
    let payload = {
      job_function: currentJobFunction,
      job_title: currentJobTitle,
    };
    console.log("payload", payload);
    captureEvent("EXU_Settings_UpdateAIBtn", {
      job_function: currentJobFunction,
    });
    setJobFunctionFn.mutate({
      data: payload,
    });
  };

  // get integrations
  const [activeCrm, setActiveCrmId] = useState("");
  const { activeWorkspace } = userData || {};
  const [integrations, setIntegrations] = useState({});

  const [crmIntegrations, setCRMIntegrations] = useState([]);

  const getIntegrationsFn = useQuery(
    "get_integrations",
    () => {
      return getIntegrations({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      });
    },
    {
      enabled: !!activeWorkspace,
      refetchOnWindowFocus: false,
      manual: true,
      onSuccess: (data) => {
        const newData = data;
        setIntegrations(newData);
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const handleGetIntegrations = () => {
    getIntegrationsFn.refetch();
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      handleGetIntegrations();
    }
  }, [activeWorkspace]);

  useEffect(() => {
    if (integrations && integrations.installed && integrations.installed.length > 0) {
      const crmArr = integrations.installed.filter(
        (item) => crmList.includes(item.integration.key) && item.is_enabled === true
      );
      console.log("crmArr", crmArr);
      setCRMIntegrations(crmArr);
    }
  }, [integrations && integrations.installed && integrations.installed.length]);

  useEffect(() => {
    if (
      activeWorkspace?.preferences &&
      activeWorkspace?.preferences?.active_crm_workspace_integration_config_id
    ) {
      setActiveCrmId(activeWorkspace?.preferences?.active_crm_workspace_integration_config_id);
    }
  }, [activeWorkspace?.preferences]);

  return (
    <>
      <div className="border border-border rounded-[14px] overflow-hidden bg-background">
        <div className="flex p-3 justify-between items-center border-b border-border ]">
          <div>
            <span className="text-[16px] text-gray-900 mr-3">Enrichment</span>
          </div>
        </div>
        <div className="py-4 px-4  space-y-3 ">
          <div className="flex w-full">
            <div className="inline-flex items-center w-9/12">
              <span className="text-[12px] text-gray-600">Auto verify email</span>
            </div>
            <span className="text-[12px] block w-3/12">
              {setPreferencesFn.isLoading && prefType === "email_verify" ? (
                <div className="loader_circle inline-block"></div>
              ) : (
                <input
                  onChange={(e) => {
                    handleSetPreferences(e.target.checked);
                    captureEvent("EXU_Settings_AutoVerifyBtn", {
                      state: e.target.checked ? "on" : "off",
                    });
                  }}
                  type="checkbox"
                  name=""
                  id=""
                  checked={autoVerifyEmail}
                />
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="border border-border rounded-[14px] overflow-hidden bg-background mt-3">
        <div className="flex p-3 justify-between items-center border-b border-border ]">
          <div>
            <span className="text-[16px] text-gray-900 mr-3">{process.env.REACT_APP_APP_NAME} AI</span>
          </div>
        </div>
        <div className="py-4 px-4  space-y-3 ">
          <div className="">
            <Label className="text-gray-500 mb-2 inline-block">Job function</Label>
            <Select
              className="w-full"
              onValueChange={(value) => {
                setCurrentJobFunction(value);
                console.log("value", value);
              }}
              value={currentJobFunction}
            >
              <SelectTrigger className="w-full rounded-[8px]">
                <SelectValue placeholder="Select job function" />
              </SelectTrigger>
              <SelectContent>
                {jobFunctions.map((item) => (
                  <SelectItem value={item.value}>{item.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentJobFunction === "" && formHasError && (
              <p className="text-red-400  mt-2 text-[12px]">Please select a job function</p>
            )}
            <Label className="text-gray-500 inline-block mt-4 mb-2">
              Job title <span className="text-red-400">*</span>
            </Label>
            <Input
              onChange={(e) => {
                setCurrentJobTitle(e.target.value);
              }}
              onKeyUp={(e) => {
                setCurrentJobTitle(e.target.value);
              }}
              defaultValue={currentJobTitle}
            />
            {currentJobTitle === "" && formHasError && (
              <p className="text-red-400 mt-2 text-[12px]">Please enter your job title</p>
            )}

            <div className="flex justify-end mt-1">
              <Button
                variant="outline"
                className="rounded-[8px] mt-3"
                onClick={setJobFunctionFn.isLoading ? void 0 : handleSetJobFunction}
              >
                {" "}
                Update
                {setJobFunctionFn.isLoading && <span className="loader_circle ml-2"></span>}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {crmIntegrations && crmIntegrations.length > 0 && (
        <div className="border border-border rounded-[14px] overflow-hidden bg-background mt-3">
          <div className="flex p-3 justify-between items-center border-b border-border ]">
            <div>
              <span className="text-[16px] text-gray-900 mr-3">CRM integrations</span>
            </div>
          </div>
          <div className="py-4 px-4  space-y-3 ">
            <div className="">
              <p className="text-sm mb-2 text-gray-600">Select CRM integrations</p>
              <select
                defaultValue={activeCrm ? activeCrm : crmIntegrations[0].id}
                value={activeCrm ? activeCrm : crmIntegrations[0].id}
                onChange={(e) => {
                  setActiveCrmId(e.target.value);
                }}
                className="flex h-9 w-full bg-background rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {crmIntegrations.map((item, index) => (
                  <option value={item.id} selected={index === 0}>
                    {item.name}
                  </option>
                ))}
              </select>

              <div className="flex justify-end mt-4">
                <Button
                  disabled={setPreferencesFn.isLoading}
                  variant="outline"
                  onClick={() => {
                    // handleSetPreferences(activeCrm, "crm_select");
                    if (setWorkspacePreferencesFn.isLoading) {
                      return;
                    }
                    setWorkspacePreferencesFn.mutate({
                      data: {
                        preferences: {
                          active_crm_workspace_integration_config_id: +activeCrm,
                        },
                      },
                      options: {
                        headers: { "X-Workspace-Id": activeWorkspace?.id },
                      },
                    });
                  }}
                >
                  Submit{" "}
                  {setWorkspacePreferencesFn.isLoading && (
                    <div className="loader_circle inline-block ml-2"></div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
